// Utility to determine MenuId from URL slug
import { MenuId } from './enum';
import { menuSlugs, type Locale } from './localized-paths';

/**
 * Maps a URL slug (first segment) to its corresponding MenuId
 * Supports both English and Indonesian slugs
 * 
 * @param firstSlug - The first segment of the URL path
 * @param locale - Current locale (defaults to 'id')
 * @returns MenuId if found, undefined otherwise
 * 
 * @example
 * getMenuIdFromSlug('layanan', 'id') // Returns MenuId.Services
 * getMenuIdFromSlug('services', 'en') // Returns MenuId.Services
 * getMenuIdFromSlug('tentang-kami', 'id') // Returns MenuId.AboutUs
 */
export function getMenuIdFromSlug(firstSlug: string | undefined, locale: Locale = 'id'): MenuId | undefined {
  if (!firstSlug) return undefined;

  // Check current locale first
  const currentLocaleMap = menuSlugs[locale];
  for (const [menuId, slug] of Object.entries(currentLocaleMap)) {
    if (slug === firstSlug) {
      return menuId as MenuId;
    }
  }

  // Fallback: check other locale (useful for edge cases)
  const otherLocale: Locale = locale === 'id' ? 'en' : 'id';
  const otherLocaleMap = menuSlugs[otherLocale];
  for (const [menuId, slug] of Object.entries(otherLocaleMap)) {
    if (slug === firstSlug) {
      return menuId as MenuId;
    }
  }

  return undefined;
}

/**
 * Checks if a slug path belongs to a specific menu
 * 
 * @param slugArray - Full slug array from URL
 * @param menuId - MenuId to check against
 * @param locale - Current locale
 * @returns true if the path belongs to the menu
 */
export function isMenuPath(slugArray: string[], menuId: MenuId, locale: Locale = 'id'): boolean {
  if (slugArray.length === 0) return false;
  
  const detectedMenuId = getMenuIdFromSlug(slugArray[0], locale);
  return detectedMenuId === menuId;
}

/**
 * Gets all possible slug variations for a MenuId across locales
 * 
 * @param menuId - MenuId to get slugs for
 * @returns Array of slug strings for all locales
 */
export function getMenuSlugs(menuId: MenuId): string[] {
  return Object.values(menuSlugs).map(localeMap => localeMap[menuId]);
}
